import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="5453" className={cn("flex gap-4", isMobile ? "flex-col" : "flex-row")}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-muted/50 animate-pulse rounded-full" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col space-y-4 p-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col space-y-2">
            {item.children && item.children.length > 0 ? <>
                <div className="font-sans font-semibold text-foreground/80 px-2 tracking-tight">{item.label}</div>
                <div className="pl-4 flex flex-col space-y-2 border-l border-border/40 ml-2">
                  {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-sm font-sans text-muted-foreground hover:text-primary transition-colors py-1" onClick={closeMobileMenu}>
                      {child.label}
                    </Link>)}
                </div>
              </> : <Link to={item.href || "#"} className="text-lg font-sans font-bold text-foreground hover:text-primary transition-colors px-2" onClick={closeMobileMenu}>
                {item.label}
              </Link>}
          </div>)}
      </nav>;
  }
  return <NavigationMenu>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[state=open]:bg-transparent data-[state=open]:text-primary text-foreground font-sans font-semibold tracking-tight">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-popover/95 backdrop-blur-lg">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-all hover:bg-muted hover:text-accent-foreground focus:bg-muted focus:text-accent-foreground">
                            <div className="text-sm font-sans font-semibold leading-none">{child.label}</div>
                            <p className="line-clamp-2 text-sm font-sans leading-snug text-muted-foreground mt-1">
                              {}
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary text-foreground font-sans font-semibold tracking-tight")}>
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="clinic-header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-md" : "bg-background border-transparent text-foreground")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex-shrink-0">
          <span className="text-xl font-serif font-bold tracking-tight text-foreground">
            Freetown Family Clinic (FFC)- Walk-in, Telehealth, and In-patient
          </span>
        </Link>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="104" className="header-menu">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:block">
          <Button asChild variant="secondary" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-sans font-bold tracking-wide px-6 rounded-full shadow-sm hover:shadow-md transition-all duration-300">
            <Link to="#">Book Appointment</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-primary hover:bg-muted/50 rounded-full" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="lg:hidden absolute top-full left-0 w-full bg-background/98 backdrop-blur-xl border-b border-border shadow-2xl animate-in slide-in-from-top-5 fade-in duration-300 h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="container mx-auto py-4 px-4 flex flex-col gap-6">
            <MenuProvider menu_id="104" className="mobile-menu">
              <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            <div className="pt-4 border-t border-border/50">
              <Button asChild variant="secondary" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 font-sans font-bold rounded-full py-6">
                <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
                  Book Appointment
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}